import { Component, EventEmitter, Input, Output, OnChanges } from '@angular/core'
import { UserReview } from 'src/app/shared/services/ppv.api'

export type ReviewMode = 'reviewee' | 'reviewer' | 'merged'

export interface ReviewTemplateOption {
   label: string
   value: string
}

export interface ReviewTemplateQuestionNotes {
   required?: boolean
   maxLength?: number
   revieweeLabel?: string
   reviewerLabel?: string
}

export interface ReviewTemplateQuestionOtherText {
   maxLength?: number
   revieweeLabel?: string
   reviewerLabel?: string
}

export interface ReviewTemplateTableColumn {
   label: string
   field: string
   type: 'text' | 'textarea' | 'select' | 'date'
   options?: ReviewTemplateOption[]
}

export interface ReviewTemplateQuestion {
   id: string
   type: 'radio' | 'checkbox' | 'textarea' | 'static' | 'table' | 'criteriaList'
   required?: boolean
   maxSelections?: number
   hasOther?: boolean
   labelIsHtml?: boolean
   revieweeLabel?: string
   reviewerLabel?: string
   revieweeOptions?: ReviewTemplateOption[]
   reviewerOptions?: ReviewTemplateOption[]
   options?: ReviewTemplateOption[] // shared (checkboxes)
   notes?: ReviewTemplateQuestionNotes
   otherText?: ReviewTemplateQuestionOtherText
   maxLength?: number
   requiresNotesForValues?: string[]
   notApplicableGroup?: string
   notApplicableGroupTitle?: string
   showNotApplicableGroupButton?: boolean
   maxRows?: number
   tableColumns?: ReviewTemplateTableColumn[]
}

export interface ReviewTemplateSection {
   id: string
   revieweeTitle: string
   reviewerTitle: string
   allowSectionNotApplicable?: boolean
   hideWhenNoAnswers?: boolean
   cardGroupId?: string
   cardGroupTitle?: string
   questions: ReviewTemplateQuestion[]
}

export interface ReviewTemplate {
   reviewType: string
   sections: ReviewTemplateSection[]
}

export interface AdditionalResponsibility {
   id: number
   responsibilityText: string
}

@Component({
   selector: 'review-assessment-sections',
   templateUrl: './review-assessment-sections.component.html',
   styleUrls: ['./review-assessment-sections.component.scss'],
   standalone: false,
})
export class ReviewAssessmentSectionsComponent implements OnChanges {
   @Input() userReview: UserReview
   @Input() template: ReviewTemplate
   @Input() mode: ReviewMode = 'reviewee'

   @Input() reviewType: string = 'DVM'
   @Input() additionalResponsibilities: any[] = []
   @Input() includeCareerExploration = true
   @Output() includeCareerExplorationChanged = new EventEmitter<boolean>()

   validationErrors = new Set<string>()

   @Input() canEditReviewee: boolean = false
   @Input() canEditReviewer: boolean = false
   @Input() isPrimaryReviewee: boolean

   @Output() dataChanged = new EventEmitter<Partial<UserReview>>()

   sectionGroups: ReviewTemplateSection[][] = []

   performanceOptions = [
      { label: 'Sets the Standard for Others', value: 'sets_the_standard_for_others' },
      { label: 'Successful', value: 'successful' },
      { label: 'Opportunity for Improvement', value: 'opportunity_for_improvement' },
      { label: 'Not Successful', value: 'not_successful' },
      { label: 'Not Applicable', value: 'not_applicable' },
   ]

   private OTHER = '__other__'

   private key(sectionId: string, qId: string, who: 'reviewee' | 'reviewer') {
      return `${who}:${sectionId}:${qId}`
   }

   private snakeToCamel(id: string): string {
      return id.replace(/_([a-z0-9])/g, (_, c) => String(c).toUpperCase())
   }

   private propName(questionId: string, respondent: 'reviewee' | 'reviewer', suffix?: 'Notes' | 'Other'): string {
      const base = this.snakeToCamel(questionId)
      const withSuffix = suffix ? `${base}${suffix}` : base
      return respondent === 'reviewer' ? `${withSuffix}COS` : withSuffix
   }

   ngOnChanges(): void {
      this.sectionGroups = this.buildSectionGroups()
   }

   getValue(questionId: string, respondent: 'reviewee' | 'reviewer'): any {
      if (!this.userReview) return null
      return (this.userReview as any)[this.propName(questionId, respondent)]
   }

   getNotes(questionId: string, respondent: 'reviewee' | 'reviewer'): string {
      if (!this.userReview) return ''
      return ((this.userReview as any)[this.propName(questionId, respondent, 'Notes')] ?? '') as string
   }

   getOtherText(questionId: string, respondent: 'reviewee' | 'reviewer'): string {
      if (!this.userReview) return ''
      return ((this.userReview as any)[this.propName(questionId, respondent, 'Other')] ?? '') as string
   }

   isAdditionalResponsibilitiesSection(section: ReviewTemplateSection): boolean {
      return section.id === 'section_additional_performance_criteria'
   }

   trackByResponsibilityId(_: number, r: any): number {
      return r.id
   }

   additionalResponsibilityRequiresNotes(id: number, who: 'reviewee' | 'reviewer'): boolean {
      const value = this.getValue(`additional_responsibility_${id}`, who)
      return ['sets_the_standard_for_others', 'not_successful'].includes(String(value))
   }

   setValue(questionId: string, respondent: 'reviewee' | 'reviewer', value: any): void {
      const prop = this.propName(questionId, respondent)
      this.dataChanged.emit({ [prop]: value } as any)
   }

   setNotes(questionId: string, respondent: 'reviewee' | 'reviewer', notes: string): void {
      const prop = this.propName(questionId, respondent, 'Notes')
      this.dataChanged.emit({ [prop]: notes } as any)
   }

   setOtherText(questionId: string, respondent: 'reviewee' | 'reviewer', text: string): void {
      const prop = this.propName(questionId, respondent, 'Other')
      this.dataChanged.emit({ [prop]: text } as any)
   }

   isChecked(questionId: string, respondent: 'reviewee' | 'reviewer', value: string): boolean {
      const current = (this.getValue(questionId, respondent) ?? []) as string[]
      return Array.isArray(current) ? current.includes(value) : false
   }

   hasText(v: unknown): boolean {
      return typeof v === 'string' && v.trim().length > 0
   }

   isLimitReached(question: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer', value: string): boolean {
      const max = question.maxSelections ?? 0
      if (!max) return false
      const current = (this.getValue(question.id, respondent) ?? []) as string[]
      if (!Array.isArray(current)) return false
      if (current.includes(value)) return false
      const otherChecked = !!this.getOtherText(question.id, respondent)
      return current.length + (otherChecked ? 1 : 0) >= max
   }

   toggleCheckbox(question: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer', value: string, checked: boolean): void {
      const current = ((this.getValue(question.id, respondent) ?? []) as string[]) ?? []
      const next = Array.isArray(current) ? [...current] : []

      if (checked) {
         if (!next.includes(value)) next.push(value)
      } else {
         const idx = next.indexOf(value)
         if (idx >= 0) next.splice(idx, 1)
      }

      this.setValue(question.id, respondent, next)
   }

   // "Other" is represented by Other text being non-empty, consistent with the old implementation.
   toggleOther(question: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer', checked: boolean): void {
      if (!checked) {
         this.setOtherText(question.id, respondent, '')
      } else {
         // keep existing text if any; otherwise seed empty string so UI opens textbox
         const existing = this.getOtherText(question.id, respondent)
         this.setOtherText(question.id, respondent, existing ?? '')
      }
   }

   canEdit(respondent: 'reviewee' | 'reviewer'): boolean {
      return respondent === 'reviewee' ? this.canEditReviewee : this.canEditReviewer
   }

   sectionTitle(section: ReviewTemplateSection, respondent: 'reviewee' | 'reviewer'): string {
      return respondent === 'reviewee' ? section.revieweeTitle : section.reviewerTitle
   }

   shouldShowSection(section: ReviewTemplateSection): boolean {
      if (this.isAdditionalResponsibilitiesSection(section)) {
         return (this.additionalResponsibilities?.length ?? 0) > 0
      }

      if (!section?.hideWhenNoAnswers) return true

      const respondents: Array<'reviewee' | 'reviewer'> = this.mode === 'merged' ? ['reviewee', 'reviewer'] : [this.mode]

      return (section.questions ?? []).some((q) =>
         respondents.some((who) => {
            if (q.type === 'static') return false
            if (q.type === 'table') return this.tableHasAtLeastOneCompleteRow(q, who)

            const value = this.getValue(q.id, who)
            const notes = this.getNotes(q.id, who)
            const otherText = this.getOtherText(q.id, who)

            if (Array.isArray(value)) return value.length > 0

            return (
               (value != null && String(value).trim() !== '') ||
               (notes != null && String(notes).trim() !== '') ||
               (otherText != null && String(otherText).trim() !== '')
            )
         })
      )
   }

   sectionCardGroups(): ReviewTemplateSection[][] {
      const groups: ReviewTemplateSection[][] = []
      const groupedIds = new Set<string>()

      for (const section of this.template?.sections ?? []) {
         if (!section.cardGroupId) {
            groups.push([section])
            continue
         }

         if (groupedIds.has(section.cardGroupId)) continue

         const matchingSections = (this.template?.sections ?? []).filter((s) => s.cardGroupId === section.cardGroupId)
         groups.push(matchingSections)
         groupedIds.add(section.cardGroupId)
      }

      return groups
   }

   cardTitle(sectionGroup: ReviewTemplateSection[], respondent: 'reviewee' | 'reviewer'): string {
      const first = sectionGroup?.[0]
      if (!first) return ''
      return first.cardGroupTitle || this.sectionTitle(first, respondent)
   }

   groupedSectionTitle(section: ReviewTemplateSection, respondent: 'reviewee' | 'reviewer'): string {
      return this.sectionTitle(section, respondent).replace(/^Clinical Skills - /, '')
   }

   questionLabel(q: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer'): string {
      return respondent === 'reviewee' ? (q.revieweeLabel ?? '') : (q.reviewerLabel ?? '')
   }

   radioOptions(q: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer'): ReviewTemplateOption[] {
      return respondent === 'reviewee' ? (q.revieweeOptions ?? []) : (q.reviewerOptions ?? [])
   }

   isOtherSelected(questionId: string, who: 'reviewee' | 'reviewer'): boolean {
      const val = this.getValue(questionId, who)
      return Array.isArray(val) && val.includes(this.OTHER)
   }

   toggleOtherSelected(q: any, who: 'reviewee' | 'reviewer', checked: boolean) {
      const current = this.getValue(q.id, who)
      const arr = Array.isArray(current) ? [...current] : []

      const next = checked ? Array.from(new Set([...arr, this.OTHER])) : arr.filter((x) => x !== this.OTHER)

      this.setValue(q.id, who, next)

      // If they unchecked Other, clear the text too
      if (!checked) {
         this.setOtherText(q.id, who, null)
      }
   }

   getTableRows(questionId: string, respondent: 'reviewee' | 'reviewer'): any[] {
      const value = this.getValue(questionId, respondent)
      if (Array.isArray(value)) return value
      if (typeof value === 'string' && value.trim().startsWith('[')) {
         try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
         } catch {}
      }
      return []
   }

   setTableRows(questionId: string, respondent: 'reviewee' | 'reviewer', rows: any[]): void {
      this.setValue(questionId, respondent, rows)
   }

   addTableRow(question: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer'): void {
      const rows = [...this.getTableRows(question.id, respondent)]
      if (question.maxRows && rows.length >= question.maxRows) return

      const row: any = {}
      for (const col of question.tableColumns ?? []) {
         row[col.field] = ''
      }
      rows.push(row)
      this.setTableRows(question.id, respondent, rows)
   }

   removeTableRow(question: ReviewTemplateQuestion, respondent: 'reviewee' | 'reviewer', index: number): void {
      const rows = [...this.getTableRows(question.id, respondent)]
      rows.splice(index, 1)
      this.setTableRows(question.id, respondent, rows)
   }

   getTableCell(questionId: string, respondent: 'reviewee' | 'reviewer', rowIndex: number, field: string): string {
      const rows = this.getTableRows(questionId, respondent)
      return rows?.[rowIndex]?.[field] ?? ''
   }

   setTableCell(questionId: string, respondent: 'reviewee' | 'reviewer', rowIndex: number, field: string, value: string): void {
      const rows = this.getTableRows(questionId, respondent)

      if (!rows[rowIndex]) {
         rows[rowIndex] = {}
      }

      rows[rowIndex][field] = value
      this.setValue(questionId, respondent, rows)
   }

   private tableHasAtLeastOneCompleteRow(q: ReviewTemplateQuestion, who: 'reviewee' | 'reviewer'): boolean {
      const rows = this.getTableRows(q.id, who)
      if (!rows.length) return false
      const columns = q.tableColumns ?? []
      return rows.some((row) => columns.some((col) => String(row?.[col.field] ?? '').trim().length > 0))
   }

   getCriteriaRows(questionId: string, who: 'reviewee' | 'reviewer'): any[] {
      return this.getTableRows(questionId, who)
   }

   addCriteria(q: ReviewTemplateQuestion, who: 'reviewee' | 'reviewer'): void {
      const rows = [...this.getCriteriaRows(q.id, 'reviewer')]

      rows.push({
         title: `Description ${rows.length + 1}`,
         reviewerRating: '',
         reviewerNotes: '',
         revieweeRating: '',
         revieweeNotes: '',
      })

      this.setTableRows(q.id, 'reviewer', rows)
   }

   removeCriteria(q: ReviewTemplateQuestion, who: 'reviewee' | 'reviewer', index: number): void {
      this.removeTableRow(q, who, index)
   }

   setCriteriaCell(q: ReviewTemplateQuestion, who: 'reviewee' | 'reviewer', index: number, field: string, value: string): void {
      this.setTableCell(q.id, who, index, field, value)
   }

   criteriaRequiresNotes(value: string): boolean {
      return ['not_successful', 'sets_the_standard_for_others'].includes(String(value))
   }

   public shouldRequireNotes(q: ReviewTemplateQuestion, who: 'reviewee' | 'reviewer'): boolean {
      const value = this.getValue(q.id, who)
      return !!q.requiresNotesForValues?.some((v) => String(v) === String(value))
   }

   getNotesLabel(q: ReviewTemplateQuestion, who: 'reviewee' | 'reviewer'): string {
      const originalLabel = who === 'reviewee' ? q.notes?.revieweeLabel : q.notes?.reviewerLabel

      return this.shouldRequireNotes(q, who) ? 'Please explain why' : (originalLabel ?? '')
   }

   getRevieweeLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'HM':
            return short ? 'HM' : 'Hospital Manager'
         case 'COS':
            return short ? 'COS' : 'Chief of Staff'
         case 'RM':
            return short ? 'RM' : 'Regional Manager'
         case 'DO':
            return short ? 'DO' : 'Director of Operations'
         case 'RECEPTION':
            return short ? 'RECEPTION' : 'Reception Team Member'
         case 'CROSSTRAINED':
         case 'CROSS-TRAINED':
            return short ? 'CROSS-TRAINED' : 'Cross-Trained Team Member'
         case 'CLINICAL':
            return short ? 'CLINICAL' : 'Clinical Team Member'
         case 'DVM':
         default:
            return short ? 'aDVM' : 'Associate DVM'
      }
   }

   getReviewerLabel(short: boolean = false): string {
      switch (this.reviewType) {
         case 'HM':
            return short ? 'RM' : 'Regional Manager'
         case 'COS':
            return short ? 'COSA' : 'Chief of Staff Advisor'
         case 'RM':
            return short ? 'DO' : 'Director of Operations'
         case 'DO':
            return short ? 'VP' : 'Vice President of Operations'
         case 'RECEPTION':
            return short ? 'HM' : 'Hospital Manager'
         case 'CROSSTRAINED':
         case 'CROSS-TRAINED':
            return short ? 'HM' : 'Hospital Manager'
         case 'CLINICAL':
            return short ? 'HM' : 'Hospital Manager'
         case 'DVM':
         default:
            return short ? 'COS' : 'Chief of Staff'
      }
   }

   validateRequired(who: 'reviewee' | 'reviewer'): { ok: boolean; missingFields: string[] } {
      const missingFields: string[] = []

      // clear previous errors for this role; keep other role’s errors if merged view)
      for (const k of Array.from(this.validationErrors)) {
         if (k.startsWith(`${who}:`)) this.validationErrors.delete(k)
      }

      for (const section of this.template.sections ?? []) {
         if (!this.shouldShowSection(section)) continue

         for (const q of section.questions ?? []) {
            // Skip validation for questions you hide (empty label for that role)
            if (who === 'reviewee') {
               if (q.revieweeLabel !== undefined && (!q.revieweeLabel || !q.revieweeLabel.trim())) continue
            } else {
               if (q.reviewerLabel !== undefined && (!q.reviewerLabel || !q.reviewerLabel.trim())) continue
            }

            if (q.type === 'static' || !q.required) continue

            const value = this.getValue(q.id, who)
            const otherSelected = !!q.hasOther && this.isOtherSelected(q.id, who)
            const otherText = this.getOtherText(q.id, who)

            let isValid = true

            if (q.type === 'radio') {
               isValid = !!value
            } else if (q.type === 'checkbox') {
               isValid = Array.isArray(value) && value.length > 0
               if (isValid && otherSelected) isValid = !!otherText?.trim()
            } else if (q.type === 'textarea') {
               isValid = typeof value === 'string' && value.trim().length > 0
            } else if (q.type === 'table') {
               isValid = this.tableHasAtLeastOneCompleteRow(q, who)
            }

            if (isValid && this.shouldRequireNotes(q, who)) {
               isValid = !!this.getNotes(q.id, who)?.trim()
            }

            if (!isValid) {
               const id = this.key(section.id, q.id, who)
               missingFields.push(id)
               this.validationErrors.add(id)
            }
         }
      }

      // forces template to update
      this.validationErrors = new Set(this.validationErrors)

      return { ok: missingFields.length === 0, missingFields }
   }

   exportResponses(who: 'reviewee' | 'reviewer') {
      const rows: Array<{
         questionId: string
         sectionId: string
         respondentType: 'reviewee' | 'reviewer'
         responseValue: string | null
         otherText: string | null
         notes: string | null
      }> = []

      for (const section of this.template?.sections ?? []) {
         if (!this.shouldShowSection(section)) continue

         for (const q of section.questions ?? []) {
            if (q.type === 'static') continue

            const respondentType = who
            const questionId = q.id
            const sectionId = section.id

            const value = this.getValue(q.id, who)
            const notes = this.getNotes(q.id, who) ?? null

            let responseValue: string | null = null
            let otherText: string | null = null

            if (q.type === 'table') {
               responseValue = JSON.stringify(
                  this.getTableRows(q.id, who).filter((row) => (q.tableColumns ?? []).some((col) => String(row?.[col.field] ?? '').trim().length > 0))
               )
            } else if (q.type === 'checkbox') {
               // store as JSON array string (matches backend expectation)
               const arr = Array.isArray(value) ? value : []
               responseValue = JSON.stringify(arr)

               if (q.hasOther && this.isOtherSelected(q.id, who)) {
                  otherText = (this.getOtherText(q.id, who) ?? '').trim() || null
               }
            } else {
               // radio / textarea
               responseValue = value != null ? String(value) : null

               if (q.hasOther && this.isOtherSelected(q.id, who)) {
                  otherText = (this.getOtherText(q.id, who) ?? '').trim() || null
               }
            }

            // Optional: skip completely empty answers so DB stays clean
            const hasAnything =
               (responseValue && responseValue !== '[]' && responseValue.trim() !== '') ||
               (otherText && otherText.trim() !== '') ||
               (notes && notes.trim() !== '')

            if (hasAnything) {
               rows.push({
                  questionId,
                  sectionId,
                  respondentType,
                  responseValue,
                  otherText,
                  notes,
               })
            }
         }
      }

      // Dynamic Additional Responsibilities
      for (const r of this.additionalResponsibilities ?? []) {
         const questionId = `additional_responsibility_${r.id}`
         const sectionId = 'section_additional_performance_criteria'

         const value = this.getValue(questionId, who)
         const notes = this.getNotes(questionId, who) ?? null

         const responseValue = value != null ? String(value) : null

         const hasAnything = (responseValue && responseValue.trim() !== '') || (notes && notes.trim() !== '')

         if (hasAnything) {
            rows.push({
               questionId,
               sectionId,
               respondentType: who,
               responseValue,
               otherText: null,
               notes,
            })
         }
      }
      return rows
   }

   hydrateFromResponses(all: any[], who: 'reviewee' | 'reviewer') {
      const mine = (all ?? []).filter((r) => r.respondentType === who)

      for (const r of mine) {
         const qid = r.questionId
         let value: any = r.responseValue
         try {
            if (typeof value === 'string' && value.trim().startsWith('[')) {
               value = JSON.parse(value)
            }
         } catch {}

         this.setValue(qid, who, value)
         if (r.notes) this.setNotes(qid, who, r.notes)

         // Always hydrate otherText if __other__ is selected, even if text is empty
         const otherIsSelected = Array.isArray(value) && value.includes('__other__')
         if (otherIsSelected || r.otherText) {
            this.setOtherText(qid, who, r.otherText ?? '')
         }
      }
   }

   modeKey(who: 'reviewee' | 'reviewer', sectionId: string, qId: string) {
      return `${who}:${sectionId}:${qId}`
   }

   formatAnswer(q: any, who: 'reviewee' | 'reviewer'): string {
      const value = this.getValue(q.id, who)
      const notes = (this.getNotes(q.id, who) ?? '').trim()

      // Checkbox: turn selections into comma list
      if (q.type === 'checkbox') {
         const arr = Array.isArray(value) ? value : []
         const labels = (q.options ?? []).filter((o: any) => arr.includes(o.value)).map((o: any) => o.label)

         // Other
         if (q.hasOther && this.isOtherSelected(q.id, who)) {
            const other = (this.getOtherText(q.id, who) ?? '').trim()
            if (other) labels.push(`Other: ${other}`)
            else labels.push('Other')
         }

         return labels.length ? labels.join(', ') : '—'
      }

      // Table: format row answers
      if (q.type === 'table') {
         const rows = this.getTableRows(q.id, who).filter((row) =>
            (q.tableColumns ?? []).some((col: any) => String(row?.[col.field] ?? '').trim().length > 0)
         )

         if (!rows.length) return '—'

         return rows
            .map((row, idx) => {
               const values = (q.tableColumns ?? [])
                  .map((col: any) => {
                     let raw = row?.[col.field]
                     if (col.type === 'select') {
                        const match = (col.options ?? []).find((o: any) => String(o.value) === String(raw))
                        raw = match?.label ?? raw
                     }
                     return raw != null && String(raw).trim() !== ''
                        ? `<i>${col.label}:</i> ${col.type == 'date' ? new Date(raw).toLocaleDateString() : raw}`
                        : null
                  })
                  .filter(Boolean)
                  .join('<br>')
               return `${idx + 1}. ${values}`
            })
            .join('<br><br>')
      }

      // Radio: map stored value to label if possible
      if (q.type === 'radio') {
         const options = who === 'reviewee' ? (q.revieweeOptions ?? q.options ?? []) : (q.reviewerOptions ?? q.options ?? [])
         const opt = options.find((o: any) => String(o.value) === String(value))
         return opt?.label ?? (value != null && String(value).trim() !== '' ? String(value) : '—')
      }

      // Textarea / text
      return value != null && String(value).trim() !== '' ? String(value) : '—'
   }

   isSectionAllNotApplicable(section: ReviewTemplateSection, respondent: 'reviewee' | 'reviewer'): boolean {
      const radioQuestions = (section.questions ?? []).filter(
         (q) => q.type === 'radio' && this.radioOptions(q, respondent).some((opt) => opt.value === 'not_applicable')
      )

      return radioQuestions.length > 0 && radioQuestions.every((q) => this.getValue(q.id, respondent) === 'not_applicable')
   }

   markSectionNotApplicable(section: ReviewTemplateSection, respondent: 'reviewee' | 'reviewer'): void {
      for (const q of section.questions ?? []) {
         if (q.type !== 'radio') continue
         const hasNotApplicable = this.radioOptions(q, respondent).some((opt) => opt.value === 'not_applicable')
         if (hasNotApplicable) {
            this.setValue(q.id, respondent, 'not_applicable')
         }
      }
   }

   hideExplorationSection(): void {
      const next = !this.includeCareerExploration
      this.includeCareerExploration = next
      this.includeCareerExplorationChanged.emit(next)
   }

   private buildSectionGroups(): ReviewTemplateSection[][] {
      const groups: ReviewTemplateSection[][] = []
      const handledGroupIds = new Set<string>()

      for (const section of this.template?.sections ?? []) {
         if (!section.cardGroupId) {
            groups.push([section])
            continue
         }

         if (handledGroupIds.has(section.cardGroupId)) continue

         groups.push((this.template?.sections ?? []).filter((s) => s.cardGroupId === section.cardGroupId))
         handledGroupIds.add(section.cardGroupId)
      }

      return groups
   }

   showSectionFor(section: ReviewTemplateSection, who: 'reviewee' | 'reviewer'): boolean {
      if (section.id === 'section_growth_and_career_path' && who === 'reviewer' && !this.includeCareerExploration && this.isPrimaryReviewee) {
         return false
      }

      return who === 'reviewee' ? this.hasText(section.revieweeTitle) : this.hasText(section.reviewerTitle)
   }

   showSectionGroup(sectionGroup: ReviewTemplateSection[]): boolean {
      if ((sectionGroup ?? []).some((s) => this.isAdditionalResponsibilitiesSection(s))) {
         return (this.additionalResponsibilities?.length ?? 0) > 0
      }

      if (this.mode === 'reviewee') {
         return sectionGroup.some((s) => this.showSectionFor(s, 'reviewee'))
      }

      if (this.mode === 'reviewer') {
         return sectionGroup.some((s) => this.showSectionFor(s, 'reviewer'))
      }

      return sectionGroup.some((s) => this.showSectionFor(s, 'reviewee') || this.showSectionFor(s, 'reviewer'))
   }

   shouldShowSectionGroup(sectionGroup: ReviewTemplateSection[]): boolean {
      return (sectionGroup ?? []).some(
         (section) => this.shouldShowSection(section) && (this.canEditReviewee || section.id !== 'section_job_description')
      )
   }

   trackByRowIndex(index: number): number {
      return index
   }

   trackBySectionGroup = (_: number, group: ReviewTemplateSection[]) => group?.[0]?.cardGroupId || group?.[0]?.id

   trackBySectionId = (_: number, section: { id: string }) => section.id
}
